/**
 * @deprecated This file is deprecated. Please use the new service structure:
 * - Import from "./services" or "./services/index"
 * - Use userService instead of userApi
 * - Use userDocumentService instead of userDocumentApi
 * - Use baseApiService instead of apiService
 *
 * This file will be removed in a future version.
 */
import { config } from "../application/common/static-config";
import { buildBlobProxyUrl } from "../utils/blobProxyUrl";

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = config.baseURL;
  }

  private getAuthToken(): string | null {
    return (
      localStorage.getItem("bestekJwtToken") ||
      sessionStorage.getItem("bestekJwtToken")
    );
  }

  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem("bestekJwtToken");
        sessionStorage.removeItem("bestekJwtToken");
        window.location.href = "/login";
        throw new Error("Authentication expired");
      }

      const errorData = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorData}`);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "GET",
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "POST",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async postWithTimeout<T>(
    endpoint: string,
    data?: unknown,
    timeoutMs: number = 600000
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: "POST",
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error(`Request timeout after ${timeoutMs / 1000} seconds`);
      }
      throw error;
    }
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "PUT",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async putWithTimeout<T>(
    endpoint: string,
    data?: unknown,
    timeoutMs: number = 600000
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error(`Request timeout after ${timeoutMs / 1000} seconds`);
      }
      throw error;
    }
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "PATCH",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "DELETE",
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  // Upload files with auth
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const token = this.getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "POST",
      headers,
      body: formData,
    });

    return this.handleResponse<T>(response);
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Generic API Response interface - can handle any data structure
export interface ApiResponse<T = Record<string, unknown>[]> {
  success: boolean;
  data: T; // Generic array of objects - can be anything
  message: string;
}

// Interface for data extraction/analysis response
export interface DataAnalysisOutput {
  file_name: string;
  extracted_data: {
    [key: string]:
      | string
      | number
      | boolean
      | string[]
      | Record<string, unknown>;
  };
  analysis_summary?: string;
  data_points?: {
    field_name: string;
    field_value: string | number | boolean;
    confidence_score?: number;
    data_type: "text" | "number" | "date" | "boolean" | "array" | "object";
  }[];
  tables?: {
    table_name: string;
    headers: string[];
    rows: (string | number)[][];
  }[];
  key_insights?: string[];
}

// Interface for document summarization response
export interface DocumentSummarizationOutput {
  file_name: string;
  overall_summary: string;
  categories: AnalysisCategory[];
}

// Flexible workflow response that can handle different data structures
export type WorkflowResponse = ApiResponse<
  | {
      output: DocumentSummarizationOutput;
    }
  | {
      output: DocumentSummarizationOutput;
    }[]
  | {
      output: DataAnalysisOutput;
    }
  | {
      output: DataAnalysisOutput;
    }[]
  | {
      word: string;
      pdf: string;
    }
  | {
      request_id: string;
      status: "pending" | "processing" | "completed";
      specifications: string[];
    }[]
>;

// Specific response types for different screens
export type DocumentAnalysisResponse = WorkflowResponse;
export type RequestSpecsWorkflowResponse = WorkflowResponse;

export type UserListResponse = ApiResponse<
  {
    id: string;
    name: string;
    email: string;
    role: string;
  }[]
>;

export type DashboardStatsResponse = ApiResponse<
  {
    metric: string;
    value: number;
    trend: "up" | "down" | "stable";
  }[]
>;

export type CompareSpecsResponse = ApiResponse<
  {
    comparison_id: string;
    results: {
      similarities: string[];
      differences: string[];
      score: number;
    };
  }[]
>;

export type RequestSpecsResponse = ApiResponse<
  {
    request_id: string;
    status: "pending" | "processing" | "completed";
    specifications: string[];
  }[]
>;

// Keep the existing analysis interfaces for backward compatibility
export interface AnalysisOutput {
  file_name: string;
  overall_summary: string;
  categories: AnalysisCategory[];
}

export interface AnalysisCategory {
  category_name: string;
  summary_points: string[];
  subcategories: AnalysisSubcategory[];
}

export interface AnalysisSubcategory {
  subcategory_name: string;
  summary_points: string[];
}

export interface AnalysisResult {
  output: AnalysisOutput;
}

// RequestSpecs response data structure
export interface RequestSpecsResult {
  word: string; // URL to Word document
  pdf: string; // URL to PDF document
}

// Deprecated - use DocumentAnalysisResponse instead
export type AnalysisApiResponse = DocumentAnalysisResponse;

// DTOs matching your backend
export interface FileUploadRequestDto {
  base64File: string;
  fileName: string;
  contentType: string;
}

export interface FileUploadUrlDto {
  fileUrl: string;
  fileName: string;
  fileSize?: string;
  mimeType?: string;
}

export interface CreateUserDocumentDto {
  userId: string;
  tittle: string; // Note: keeping the typo as in your backend DTO
  description?: string;
  question?: string;
  workflowType?: string;
  files: FileUploadRequestDto[];
  exsitingFiles?: WorkflowDocumentDto[];
  typeOfDocument: string;
  companyName?: string;
  companyId?: string;
  selectedRules?: string[]; // Array of selected rule IDs
}

export interface UpdateUserDocumentDto {
  userId: string;
  tittle: string; // Note: keeping the typo as in your backend DTO
  description?: string;
  question?: string;
  workflowType?: string;
  files: FileUploadUrlDto[];
  typeOfDocument: string;
  companyName?: string;
  companyId?: string;
  selectedRules?: string[]; // Array of selected rule IDs
}

export interface CreateDocumentContradictionDto {
  userId: string;
  companyId?: string;
  title?: string;
  workflowType?: string;
  description?: string;
  referenceFile?: FileUploadRequestDto;
  referenceFileUrl?: string;
  targetFile?: FileUploadRequestDto;
  targetFileUrl?: string;
}

export interface WorkflowResponseDto {
  success: boolean;
  data?: unknown;
  message?: string;
  job_id?: string;
  status?: string;
}

export interface JobStatusResponseDto {
  job_id: string;
  status: string;
  message?: string;
  data?: unknown;
}

export interface WorkflowFileDto {
  fileName: string;
  fileUrl: string;
  fileType?: string;
}

export interface WorkflowHistoryDetailDto {
  _id: string;
  title: string;
  userId: string;
  companyId: string;
  workflow: string;
  workflowType: string;
  instruction?: string;
  timestamp: string;
  jobId?: string;
  status: string;
  files: WorkflowFileDto[];
  response?: any;
}

export interface UserDocumentResponse {
  id: string;
  userId: string;
  tittle: string;
  description?: string;
  question?: string;
  typeOfDocument: string;
  documentLink: string;
  documentSize: number;
  createdBy?: string;
  createdDate: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
  isDeleted: boolean;
  isActive: boolean;
  companyName?: string;
  companyId?: string;
  jobId?: string;
  status?: string;
}

const normalizeDocument = (
  document: UserDocumentResponse,
): UserDocumentResponse => ({
  ...document,
  documentLink: buildBlobProxyUrl(document.documentLink),
});

// User interfaces
export interface UserResponse {
  id: string;
  userId: string;
  roleId: string;
  roleName: string;
  userName: string;
  email: string;
  isActive?: boolean;
}

export interface UpdateUserStatusDto {
  isActive: boolean;
}

export interface WorkflowDocumentDto {
  url: string;
  name: string;
  size: string;
  mimeType: string;
}
// User Document specific API calls
export const userDocumentApi = {
  // Get all documents
  getAll: () =>
    apiService
      .get<UserDocumentResponse[]>("/UserDocument")
      .then((documents) => documents.map(normalizeDocument)),

  // Get document by ID
  getById: (id: string) =>
    apiService
      .get<UserDocumentResponse>(`/UserDocument/${id}`)
      .then(normalizeDocument),

  // Create new document with file upload
  create: (dto: CreateUserDocumentDto) =>
    apiService.postWithTimeout<DocumentAnalysisResponse>(
      "/UserDocument",
      dto,
      600000
    ), // 10 minutes timeout

  update: (dto: UpdateUserDocumentDto) =>
    apiService.putWithTimeout<DocumentAnalysisResponse>(
      "/UserDocument",
      dto,
      600000
    ), // 10 minutes timeout

  // Delete document
  delete: (id: string) => apiService.delete<void>(`/UserDocument/${id}`),

  // Get documents by user email (since backend authenticates by Azure AD token and maps by email)
  getByUserEmail: (email: string) =>
    apiService
      .get<UserDocumentResponse[]>(
        `/UserDocument/by-user-email/${encodeURIComponent(email)}`
      )
      .then((documents) => documents.map(normalizeDocument)),

  // Get documents by user ID (POST request)
  getByUserId: (userId: string) => {
    // For a simple string parameter, we need to send it as a JSON string
    return apiService
      .post<UserDocumentResponse[]>(
        "/UserDocument/DocumentByUserId",
        userId // Send as JSON string - the post method will handle JSON.stringify
      )
      .then((documents) => documents.map(normalizeDocument));
  },

  // Create document contradiction analysis
  createDocumentContradiction: (dto: CreateDocumentContradictionDto) =>
    apiService.postWithTimeout<WorkflowResponseDto>(
      "/UserDocument/document-contradiction",
      dto,
      600000
    ), // 10 minutes timeout

  // Get job status for polling
  getJobStatus: (jobId: string) =>
    apiService.get<WorkflowHistoryDetailDto>(`/Workflow/status/${jobId}`),
};

// User specific API calls
export const userApi = {
  // Get all users - returns different data structure
  getAll: () => apiService.get<UserListResponse>("/User"),

  // Get user by ID
  getById: (id: string) => apiService.get<UserResponse>(`/User/${id}`),

  // Update user active status
  updateStatus: (id: string, dto: UpdateUserStatusDto) =>
    apiService.patch<void>(`/User/${id}/status`, dto),
};

// Dashboard API calls - different response structure
export const dashboardApi = {
  getStats: () => apiService.get<DashboardStatsResponse>("/Dashboard/stats"),
  getMetrics: (timeRange: string) =>
    apiService.get<DashboardStatsResponse>(
      `/Dashboard/metrics?range=${timeRange}`
    ),
};

// Compare Specs API calls - different response structure
export const compareSpecsApi = {
  compare: (spec1Id: string, spec2Id: string) =>
    apiService.postWithTimeout<CompareSpecsResponse>(
      "/CompareSpecs",
      {
        spec1Id,
        spec2Id,
      },
      600000
    ), // 10 minutes timeout
  getComparison: (comparisonId: string) =>
    apiService.get<CompareSpecsResponse>(`/CompareSpecs/${comparisonId}`),
};

// Request Specs API calls - using the same endpoints as document analysis
export const requestSpecsApi = {
  // Create new request specs - uses the same endpoint as document analysis
  create: (dto: CreateUserDocumentDto) =>
    apiService.postWithTimeout<RequestSpecsWorkflowResponse>(
      "/UserDocument",
      dto,
      600000
    ), // 10 minutes timeout

  // Update existing request specs - uses the same endpoint as document analysis
  update: (dto: UpdateUserDocumentDto) =>
    apiService.putWithTimeout<RequestSpecsWorkflowResponse>(
      "/UserDocument",
      dto,
      600000
    ), // 10 minutes timeout

  // Get status by document ID (reuses existing document API)
  getStatus: (documentId: string) =>
    apiService
      .get<UserDocumentResponse>(`/UserDocument/${documentId}`)
      .then(normalizeDocument),

  // Get all request specs (reuses existing document API)
  getAll: () =>
    apiService
      .get<UserDocumentResponse[]>("/UserDocument")
      .then((documents) => documents.map(normalizeDocument)),

  // Get by user ID (reuses existing document API)
  getByUserId: (userId: string) =>
    apiService
      .post<UserDocumentResponse[]>(
        "/UserDocument/DocumentByUserId",
        userId
      )
      .then((documents) => documents.map(normalizeDocument)),
};

// Role Permission API calls - could use generic response
export const rolePermissionApi = {
  getRoles: () =>
    apiService.get<
      ApiResponse<{ roleId: string; roleName: string; permissions: string[] }[]>
    >("/Roles"),
  updatePermissions: (roleId: string, permissions: string[]) =>
    apiService.put<ApiResponse<{ success: boolean }[]>>(
      `/Roles/${roleId}/permissions`,
      { permissions }
    ),
};

// Utility function to convert File to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Helper function to get MIME type from file extension
export const getContentType = (fileName: string): string => {
  const extension = fileName.toLowerCase().split(".").pop();
  switch (extension) {
    case "pdf":
      return "application/pdf";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      return "application/octet-stream";
  }
};

// Utility functions to determine response type
export const isDocumentAnalysisResponse = (
  data: unknown
): data is { output: AnalysisOutput } | { output: AnalysisOutput }[] => {
  if (Array.isArray(data)) {
    return (
      data.length > 0 &&
      typeof data[0] === "object" &&
      data[0] !== null &&
      "output" in data[0]
    );
  }
  return typeof data === "object" && data !== null && "output" in data;
};

export const isRequestSpecsResponse = (
  data: unknown
): data is RequestSpecsResult => {
  return (
    typeof data === "object" && data !== null && "word" in data && "pdf" in data
  );
};

export const isWorkflowResponse = (
  response: unknown
): response is WorkflowResponse => {
  return (
    typeof response === "object" &&
    response !== null &&
    "success" in response &&
    "data" in response &&
    "message" in response
  );
};

// Helper functions to determine response type
export const isDocumentSummarizationResponse = (
  response: WorkflowResponse
): response is ApiResponse<
  | { output: DocumentSummarizationOutput }
  | { output: DocumentSummarizationOutput }[]
> => {
  if (!response.success || !response.data) return false;

  const data = Array.isArray(response.data) ? response.data[0] : response.data;
  if (!data || !("output" in data)) return false;

  const output = data.output as
    | DocumentSummarizationOutput
    | DataAnalysisOutput;
  return (
    "overall_summary" in output &&
    "categories" in output &&
    Array.isArray((output as DocumentSummarizationOutput).categories)
  );
};

export const isDataAnalysisResponse = (
  response: WorkflowResponse
): response is ApiResponse<
  { output: DataAnalysisOutput } | { output: DataAnalysisOutput }[]
> => {
  if (!response.success || !response.data) return false;

  const data = Array.isArray(response.data) ? response.data[0] : response.data;
  if (!data || !("output" in data)) return false;

  const output = data.output as
    | DocumentSummarizationOutput
    | DataAnalysisOutput;
  return (
    "extracted_data" in output ||
    "data_points" in output ||
    ("analysis_summary" in output && !("categories" in output))
  );
};
